/* eslint-disable react-native/no-inline-styles */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Text,
  View,
  FlatList,
  ViewStyle,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Popover, { PopoverPlacement } from 'react-native-popover-view';
import theme from '../../common/theme';
import SelectStyles from './Select.styles';
import Icon from '../Icon/Icon';
import { useColors } from '../../hooks/useColors';

export interface ColourOption {
  value: string;
  label: string;
}

export interface SelectColourProps {
  testID: string;
  title?: string;
  editable?: boolean;
  value?: string;
  containerStyle?: ViewStyle | ViewStyle[];
  onChange?: (value: string) => void;
}

const SelectColour: React.FC<SelectColourProps> = ({
  title,
  testID,
  editable = true,
  containerStyle,
  value: valueProp,
  onChange,
}: SelectColourProps) => {
  const touchable = useRef(null);
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const styles = SelectStyles();
  const { colors } = useColors();

  const options = useMemo(() => {
    return theme.colors.pools.timers.map(color => ({
      label: color.name,
      value: color.color,
    }));
  }, []);

  const selectedColourOption = useMemo(() => {
    return options.find(option => option.value === selectedValue);
  }, [options, selectedValue]);

  const onToggleOptions = useCallback((): void => {
    if (editable) {
      setShowOptions(current => !current);
    }
  }, [editable]);

  const onRequestClosePopover = useCallback(() => {
    setShowOptions(false);
  }, []);

  const onPressOption = (value: string) => {
    setSelectedValue(value);
    onChange && onChange(value);
    setShowOptions(false);
  };
  const onPressOptionOnAndroid = (value: string) => {
    if (Platform.OS === 'android') {
      setSelectedValue(value);
      onChange && onChange(value);
      setShowOptions(false);
    }
  };

  useEffect(() => {
    if (valueProp) {
      setSelectedValue(valueProp);
    }
  }, [valueProp]);

  return (
    <View
      style={[
        containerStyle,
        {
          backgroundColor: colors.inputBackground,
          borderWidth: 2,
          borderColor: colors.background,
        },
      ]}
      testID={testID}
    >
      {!!title && <Text style={styles.inputTitleText}>{title}</Text>}
      <View>
        <TouchableOpacity
          ref={touchable}
          onPress={onToggleOptions}
          disabled={!editable}
          style={styles.inputContainer}
        >
          <View style={styles.colourValue}>
            <View
              style={[
                styles.colourContainer,
                {
                  backgroundColor: selectedValue || theme.colors.grey2,
                },
              ]}
            />
            <Text style={styles.selectedOptionText}>
              {selectedColourOption?.label || 'Select colour...'}
            </Text>
          </View>
          {editable && (
            <View style={styles.accessory}>
              <Icon
                size={20}
                color={colors.text}
                name={showOptions ? 'angle-up' : 'angle-down'}
              />
            </View>
          )}
        </TouchableOpacity>
      </View>
      <Popover
        isVisible={showOptions}
        placement={PopoverPlacement.AUTO}
        onRequestClose={onRequestClosePopover}
        from={touchable}
        backgroundStyle={styles.bgStyle}
        popoverStyle={styles.optionsContainer}
      >
        <View style={styles.optionsPopup}>
          <FlatList
            data={options}
            keyExtractor={(item, i) => `${i}-${item.value}`}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={styles.option}
                onPress={() => onPressOption(item.value)}
                onPressOut={() => onPressOptionOnAndroid(item.value)}
              >
                <View
                  style={[
                    styles.colourContainer,
                    { backgroundColor: item.value },
                  ]}
                />
                <Text>{item.label}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </Popover>
    </View>
  );
};

export default SelectColour;
